import { CardHeader, CardPreview, Drawer } from "@verde/compose-ui";
import {
  type FetchFieldInsight,
  InsightTypes,
  useFetchAllFieldInsight,
} from "@verde/entities";
import {
  AgricultureIcon,
  AlertCircleIcon,
  BirthdayFarmer,
  ExternalLinkIcon,
  EyeIcon,
  FlagIcon,
  LivestockIcon,
  SeuVerdeIcon,
} from "@verde/icons";
import { Alert, IconButton, Link, Skeleton } from "@verde/ui-core";
import React, { useMemo, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";
import BirthdayDrawer from "./birthday-drawer";

function IconMaker(advise: FetchFieldInsight) {
  switch (advise.kind) {
    case InsightTypes.CROP:
      return advise.crop_cultivation.kind === "AGRICULTURE" ? (
        <AgricultureIcon />
      ) : (
        <LivestockIcon />
      );
    case InsightTypes.WEATHER:
      return <EyeIcon />;
    case InsightTypes.BIRTHDAY:
      return <BirthdayFarmer />;
    default:
      return <FlagIcon />;
  }
}

function EmptyState() {
  return (
    <div className="flex-1 h-full p-10 gap-5 rounded bg-neutral-light-1 border border-brand-yellow">
      <div className="grid justify-center w-full">
        <img
          className="h-20"
          src="https://verde-assets.s3.sa-east-1.amazonaws.com/images/seu-verde/bud-green.png"
          alt="bud-verde"
        />
      </div>
      <div className="grid gap-3">
        <p className="self-center text-2xl font-extrabold text-brand-green-dark indent-4 ">
          Seu Verde ainda está está <br /> brotando...
        </p>
        <p className="text-lg text-brand-green-dark indent-3">
          Receba alertas de plantio, colheita e manejo, recomendações <br />
          personalizadas da sua carteira, assim que seus produtores <br />
          começarem a gerar dados.
        </p>
        <p className="text-base text-brand-green-dark text-right">
          <b>Fique tranquilo:</b> mostraremos aqui quando tivermos novidades do
          campo.
        </p>
      </div>
    </div>
  );
}

function SeuVerdeWidget({ mode = "full" }: SeuVerdeProps) {
  const [isOpenBirthday, toggleBirthday] = useReducer((value) => !value, false);
  const [selectedInsight, setSelectedInsight] = useState<number | undefined>(
    undefined,
  );
  const isShortest = useMemo(() => mode === "short", [mode]);

  const { data, isLoading } = useFetchAllFieldInsight();

  const navigate = useNavigate();

  const isEmpty = useMemo(() => {
    return !isLoading && (data?.length === 0 || !data);
  }, [data, isLoading]);

  return (
    <div className="grid">
      <div className="flex flex-col rounded bg-neutral-light-pure p-4 gap-3">
        <div className="flex-none">
          <CardHeader
            title="Seu Verde"
            icon={<SeuVerdeIcon height={28} width={28} />}
            description={
              isShortest
                ? !isEmpty
                  ? "Sugestões personalizadas do Seu Verde para sua carteira"
                  : ""
                : !isEmpty
                  ? "Seu assistente inteligente do campo: receba alertas de plantio, colheita, manejo e clima, além de dicas personalizadas para seus produtores"
                  : ""
            }
            classNames={{ icon: "h-6 w-6" }}
          />
        </div>
        {isEmpty ? (
          isShortest ? (
            <Alert icon={<AlertCircleIcon />} kind="info">
              Em breve iremos disponibilizar informações fresquinhas direto do
              campo para você!
            </Alert>
          ) : (
            EmptyState()
          )
        ) : (
          <div
            className={`grid gap-2 mt-4 ${isShortest ? "max-h-[204px]" : "max-h-[446px] items-center"}  ${!isLoading && !isEmpty ? "overflow-auto scrollbar-thin pr-2" : ""}`}
          >
            {isLoading ? (
              <Skeleton className="flex-none" height={isShortest ? 204 : 446} />
            ) : (
              data?.map((advise, index) => {
                const isBirthday = advise.kind === InsightTypes.BIRTHDAY;
                const isMoreThenOne = advise.clients_count > 1;
                return (
                  <CardPreview
                    classNames={{
                      base: `${isBirthday && "border-brand-green-light border-2"}`,
                    }}
                    key={`advise-card-${index + 1}`}
                    size="sm"
                    icon={IconMaker(advise)}
                    title={advise.title}
                    description={
                      isShortest
                        ? `(${advise.clients_count}) ${isMoreThenOne ? "produtores" : "produtor"} `
                        : advise.clients_count > 0
                          ? `Você tem ${advise.clients_count} ${isMoreThenOne ? "produtores" : "produtor"} para entrar em contato`
                          : "Inicio de manejo, ótimo momento para prospectar"
                    }
                    actions={{
                      primary: {
                        render: (
                          <span className="flex gap-2 items-center text-neutral-dark-3 font-medium text-sm">
                            {isBirthday ? (
                              <Link
                                kind="secondary"
                                size="md"
                                onClick={() => {
                                  setSelectedInsight(advise.id);
                                  toggleBirthday();
                                }}
                              >
                                <div className="flex gap-2">
                                  Ver Produtores
                                  <ExternalLinkIcon height={16} />
                                </div>
                              </Link>
                            ) : (
                              <IconButton>
                                <ExternalLinkIcon
                                  onClick={() => navigate("/verde-push")}
                                />
                              </IconButton>
                            )}
                          </span>
                        ),
                      },
                    }}
                  />
                );
              })
            )}
          </div>
        )}
      </div>
      {selectedInsight && isOpenBirthday && (
        <BirthdayDrawer
          insightFieldId={selectedInsight}
          onClose={toggleBirthday}
          open={isOpenBirthday}
        />
      )}
    </div>
  );
}

export default React.memo(SeuVerdeWidget);

export interface SeuVerdeProps {
  mode?: "short" | "full";
}
